import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { VERIFICATED_LAWYER_STATUS } from '@/shared/constants/moderationConstants';
import { FREE_LEGAL_SERVICE_ID } from '@/shared/constants/lawyerServiceConstants';
import { declination } from '@/shared/utils/declination';
import {
  SCHEMA_LANGUAGE,
  buildAbsoluteUrl,
  buildBreadcrumbId,
  buildBreadcrumbListNode,
  buildCityNode,
  buildPageId,
  buildPersonId,
  buildPostalAddress,
  buildSchemaText,
  buildServiceId,
  countryNode,
  websiteReference,
  type SchemaNode,
} from '@/shared/lib/schema';
import {
  getLawyerCity,
  getLawyerDisplayName,
  getLawyerFullName,
  getLawyerJobTitle,
  isLawyerCompany,
} from '@/entities/lawyer/lib/lawyerProfile';
import type {
  ILawyerAddress,
  ILawyerDetail,
  ILawyerLegalService,
  ILawyerWorkPlan,
} from '@/entities/lawyer/model/types/lawyer';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';

export interface LawyerSchemaContext {
  pageUrl: string;
  breadcrumbs: Breadcrumb[];
}

const SCHEMA_WEEKDAYS: string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const PRICE_RANGE_BY_LEVEL: Record<string, string> = {
  low: '₽',
  mid: '₽₽',
  high: '₽₽₽',
};

const VERIFIED_TEXT: string = 'Профиль проверен, документы подтверждены.';

const getContactAddress = (lawyer: ILawyerDetail): ILawyerAddress | undefined =>
  lawyer.currentCompany?.lawyer?.addresses?.[0] ?? lawyer.addresses?.[0];

const getExperienceYears = (lawyer: ILawyerDetail): number | null => {
  if (!lawyer.activity_date) return null;
  const startYear = new Date(lawyer.activity_date).getFullYear();
  if (Number.isNaN(startYear)) return null;
  return Math.max(new Date().getFullYear() - startYear, 0);
};

const buildPersonDescription = (lawyer: ILawyerDetail): string => {
  const years = getExperienceYears(lawyer);
  const city = getLawyerCity(lawyer)?.name;
  const head = [
    getLawyerJobTitle(lawyer),
    years === null ? '' : `стаж ${years} ${declination(years, ['год', 'года', 'лет'])}`,
    city ?? '',
  ]
    .filter(Boolean)
    .join(', ');
  const isVerified = lawyer.is_validate === VERIFICATED_LAWYER_STATUS;

  return buildSchemaText(
    [`${head}.`, isVerified ? VERIFIED_TEXT : '', lawyer.description ?? '']
      .filter(Boolean)
      .join(' '),
  );
};

const buildKnowsAbout = (lawyer: ILawyerDetail): string[] => {
  const names = [...(lawyer.mainCategory ?? []), ...(lawyer.activityCategories ?? [])]
    .map((category) => category.name)
    .filter(Boolean);
  return [...new Set(names)];
};

const buildAlumniOf = (lawyer: ILawyerDetail): SchemaNode[] => {
  const titles = (lawyer.educations ?? [])
    .map((education) => education.title?.trim())
    .filter((title): title is string => Boolean(title));
  return [...new Set(titles)].map((name) => ({ '@type': 'CollegeOrUniversity', name }));
};

const buildOpeningHours = (workPlans: ILawyerWorkPlan[] = []): SchemaNode[] => {
  const groups = new Map<string, { days: string[]; opens: string; closes: string }>();

  workPlans.forEach((plan, index) => {
    const day = SCHEMA_WEEKDAYS[index];
    if (!day || plan.is_holiday || !plan.time_start || !plan.time_end) return;
    const opens = plan.time_start.slice(0, 5);
    const closes = plan.time_end.slice(0, 5);
    const key = `${opens}-${closes}`;
    const group = groups.get(key) ?? { days: [], opens, closes };
    group.days.push(day);
    groups.set(key, group);
  });

  return [...groups.values()].map((group) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: group.days,
    opens: group.opens,
    closes: group.closes,
  }));
};

const buildRating = (lawyer: ILawyerDetail): SchemaNode | null => {
  if (!lawyer.feedbackCount || !lawyer.feedbackRating) return null;
  return {
    '@type': 'AggregateRating',
    ratingValue: Number(lawyer.feedbackRating.toFixed(1)),
    bestRating: 5,
    reviewCount: lawyer.feedbackCount,
  };
};

const buildOfferCatalog = (lawyer: ILawyerDetail): SchemaNode | null => {
  const citySlug = getLawyerCity(lawyer)?.slug;
  const categoryUrl = (...segments: string[]): SchemaNode =>
    citySlug
      ? {
          url: buildAbsoluteUrl(
            `${ROOT_CATALOG_URL}geo/${citySlug}/${segments.join('/')}/`,
          ),
        }
      : {};
  const services = lawyer.activityCategories ?? [];

  const catalogs = (lawyer.mainCategory ?? [])
    .filter((category) => category.name && category.slug)
    .map((category) => {
      const children = services.filter(
        (service) => service.parent_id === category.id && service.name && service.slug,
      );
      return {
        '@type': 'OfferCatalog',
        name: category.name,
        ...categoryUrl(category.slug),
        ...(children.length
          ? {
              itemListElement: children.map((service) => ({
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: service.name,
                  ...categoryUrl(category.slug, service.slug),
                },
              })),
            }
          : {}),
      };
    });

  if (!catalogs.length) return null;
  return { '@type': 'OfferCatalog', name: 'Специализации', itemListElement: catalogs };
};

const buildOffer = (service: ILawyerLegalService): SchemaNode | null => {
  if (!service.name) return null;

  if (service.service_id === FREE_LEGAL_SERVICE_ID) {
    return {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.period ? `${service.name}, ${service.period} мин.` : service.name,
      },
      price: 0,
      priceCurrency: 'RUB',
    };
  }

  const min = Number(service.cost_min ?? 0);
  const max = Number(service.cost_max ?? 0);
  if (!min && !max) return null;

  const itemOffered = { '@type': 'Service', name: service.name };
  if (min === max) {
    return { '@type': 'Offer', itemOffered, price: min, priceCurrency: 'RUB' };
  }

  return {
    '@type': 'Offer',
    itemOffered,
    priceSpecification: {
      '@type': 'PriceSpecification',
      ...(min ? { minPrice: min } : {}),
      ...(max ? { maxPrice: max } : {}),
      priceCurrency: 'RUB',
    },
  };
};

const buildOffers = (lawyer: ILawyerDetail): SchemaNode[] =>
  (lawyer.legalServices ?? [])
    .map(buildOffer)
    .filter((offer): offer is SchemaNode => offer !== null);

const buildAreaServed = (lawyer: ILawyerDetail): SchemaNode[] => {
  const city = getLawyerCity(lawyer)?.name;
  return [
    ...(city ? [buildCityNode(city)] : []),
    ...(lawyer.is_appointment_online ? [countryNode] : []),
  ];
};

const buildLegalServiceNode = (
  lawyer: ILawyerDetail,
  pageUrl: string,
  options: { includeDescription: boolean; employeeId?: string },
): SchemaNode => {
  const address = getContactAddress(lawyer);
  const rating = buildRating(lawyer);
  const openingHours = buildOpeningHours(lawyer.workPlans);
  const areaServed = buildAreaServed(lawyer);
  const offerCatalog = buildOfferCatalog(lawyer);
  const offers = buildOffers(lawyer);
  const priceRange = lawyer.price_level
    ? PRICE_RANGE_BY_LEVEL[lawyer.price_level]
    : undefined;
  const hasGeo = typeof address?.lat === 'number' && typeof address?.lon === 'number';

  return {
    '@type': 'LegalService',
    '@id': buildServiceId(pageUrl),
    name: getLawyerDisplayName(lawyer),
    url: pageUrl,
    ...(lawyer.urlAvatar ? { image: lawyer.urlAvatar } : {}),
    ...(options.includeDescription && lawyer.description
      ? { description: buildSchemaText(lawyer.description) }
      : {}),
    ...(options.employeeId ? { employee: { '@id': options.employeeId } } : {}),
    ...(priceRange ? { priceRange } : {}),
    ...(address?.address
      ? {
          address: buildPostalAddress({
            street: address.address,
            locality: address.city?.name,
          }),
        }
      : {}),
    ...(hasGeo
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: address?.lat,
            longitude: address?.lon,
          },
        }
      : {}),
    ...(areaServed.length ? { areaServed } : {}),
    ...(openingHours.length ? { openingHoursSpecification: openingHours } : {}),
    ...(rating ? { aggregateRating: rating } : {}),
    ...(offerCatalog ? { hasOfferCatalog: offerCatalog } : {}),
    ...(offers.length ? { makesOffer: offers } : {}),
  };
};

const buildPersonNode = (
  lawyer: ILawyerDetail,
  pageUrl: string,
  offers: SchemaNode[],
): SchemaNode => {
  const city = getLawyerCity(lawyer)?.name;
  const knowsAbout = buildKnowsAbout(lawyer);
  const alumniOf = buildAlumniOf(lawyer);

  return {
    '@type': 'Person',
    '@id': buildPersonId(pageUrl),
    name: getLawyerFullName(lawyer),
    ...(lawyer.name ? { givenName: lawyer.name } : {}),
    ...(lawyer.surname ? { familyName: lawyer.surname } : {}),
    ...(lawyer.middlename ? { additionalName: lawyer.middlename } : {}),
    jobTitle: getLawyerJobTitle(lawyer),
    ...(lawyer.urlAvatar ? { image: lawyer.urlAvatar } : {}),
    url: pageUrl,
    description: buildPersonDescription(lawyer),
    ...(knowsAbout.length ? { knowsAbout } : {}),
    ...(alumniOf.length ? { alumniOf } : {}),
    ...(city
      ? {
          workLocation: {
            '@type': 'Place',
            address: buildPostalAddress({ locality: city }),
          },
        }
      : {}),
    ...(offers.length ? { makesOffer: offers } : {}),
  };
};

const buildWebPageName = (lawyer: ILawyerDetail): string => {
  const city = getLawyerCity(lawyer)?.name;
  const subject = isLawyerCompany(lawyer)
    ? getLawyerDisplayName(lawyer)
    : `${getLawyerJobTitle(lawyer)} ${getLawyerFullName(lawyer)}`;
  return city ? `${subject}, ${city}` : subject;
};

export const buildLawyerPageGraph = (
  lawyer: ILawyerDetail,
  context: LawyerSchemaContext,
): SchemaNode[] => {
  const { pageUrl } = context;
  const isCompany = isLawyerCompany(lawyer);
  const crumbs = context.breadcrumbs
    .filter((crumb) => crumb.text)
    .map((crumb) => ({ name: crumb.text, url: crumb.to }));

  const hasService =
    isCompany ||
    Boolean(buildRating(lawyer)) ||
    Boolean(getContactAddress(lawyer)?.address);
  const personId = buildPersonId(pageUrl);
  const serviceId = buildServiceId(pageUrl);

  const webPage: SchemaNode = {
    '@type': 'WebPage',
    '@id': buildPageId(pageUrl),
    url: pageUrl,
    name: buildWebPageName(lawyer),
    isPartOf: websiteReference,
    inLanguage: SCHEMA_LANGUAGE,
    ...(crumbs.length ? { breadcrumb: { '@id': buildBreadcrumbId(pageUrl) } } : {}),
    mainEntity: { '@id': isCompany ? serviceId : personId },
  };

  const entities: SchemaNode[] = isCompany
    ? [buildLegalServiceNode(lawyer, pageUrl, { includeDescription: true })]
    : [
        buildPersonNode(lawyer, pageUrl, hasService ? [] : buildOffers(lawyer)),
        ...(hasService
          ? [
              buildLegalServiceNode(lawyer, pageUrl, {
                includeDescription: false,
                employeeId: personId,
              }),
            ]
          : []),
      ];

  return [
    webPage,
    ...entities,
    ...(crumbs.length ? [buildBreadcrumbListNode(pageUrl, crumbs)] : []),
  ];
};
